import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    Autocomplete,
    AutocompleteProps,
    AutocompleteRenderInputParams,
    FormControl,
    TextField,
    Typography,
    styled,
} from '@mui/material';
import React from 'react';
import { FieldError } from 'react-hook-form';
import { Currency, findCurrencyByCode, useCurrencies, useCurrenciesLoading } from 'stores/currency/currencyStore';
import { EColors, getColor } from 'theme';

const ITEM_HEIGHT = 34;
const ITEM_COUNT = 5;

const StyledFormControl = styled(FormControl)<{ disabled?: boolean }>(({ disabled }) => ({
    width: '100%',
    backgroundColor: disabled ? getColor(EColors.drWhite) : getColor(EColors.pureWhite),
    borderRadius: '4px',
    pointerEvents: disabled ? 'none' : 'auto',

    '&:hover .MuiInputBase-root:hover > .MuiOutlinedInput-notchedOutline': {
        borderColor: getColor(EColors.v3Dark) + ' !important',
    },
    '& .MuiFormLabel-root': {
        transform: 'translate(16px, 14px)',
        fontSize: '0.875rem',
        lineHeight: '1.5em',
        color: getColor(EColors.pureBlack, 0.5),
    },

    '& .MuiFormLabel-root.MuiInputLabel-shrink': {
        color: getColor(EColors.pureBlack, 0.6),
        fontSize: '0.875rem',
        transform: 'translate(9px, -12px) scale(1)',
        backgroundColor: getColor(EColors.pureWhite),
        padding: '0 5px',
        lineHeight: '1rem',
    },
}));

const BaseAutocomplete = (props: AutocompleteProps<Currency, false, false, false>) => <Autocomplete {...props} />;

const StyledAutocomplete = styled(BaseAutocomplete, {
    shouldForwardProp: prop => prop !== 'error' && prop !== 'disabled',
})<{ error?: boolean; disabled?: boolean }>(({ error, disabled }) => ({
    width: '100%',
    '& .MuiOutlinedInput-root': {
        height: '48px',
        backgroundColor: disabled ? getColor(EColors.drWhite) : getColor(EColors.v3Background),
        borderRadius: '12px',
        padding: '4px 9px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${error ? getColor(EColors.red) : getColor(EColors.v3Border)}`,
        borderRadius: '12px',
    },
    '& .MuiInputBase-input': {
        padding: '12.5px',
        paddingLeft: '16px',
        fontSize: '0.875rem',
        color: disabled ? getColor(EColors.pureBlack, 0.38) : getColor(EColors.pureBlack),
        WebkitTextFillColor: disabled ? getColor(EColors.pureBlack, 0.38) : getColor(EColors.pureBlack),
    },
    '& .MuiInputBase-root.Mui-disabled .MuiInputBase-input, & .MuiInputBase-input[disabled]': {
        WebkitTextFillColor: `${getColor(EColors.pureBlack, 0.38)} !important`,
        color: `${getColor(EColors.pureBlack, 0.38)} !important`,
    },
    '& .MuiInputLabel-root': {
        transform: 'translate(16px, 14px)',
        fontSize: '0.875rem',
        lineHeight: '1.5em',
        color: getColor(EColors.pureBlack, 0.5),
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        color: getColor(EColors.pureBlack, 0.6),
        fontSize: '0.875rem',
        transform: 'translate(9px, -50%) scale(1)',
        backgroundColor: getColor(EColors.pureWhite),
        padding: '0 5px',
        lineHeight: '1em',
        top: 0,
    },
    '& .MuiAutocomplete-popupIndicator': {
        color: disabled ? getColor(EColors.pureBlack, 0.38) : getColor(EColors.pureBlack, 0.5),
    },
    '& .MuiInputBase-root.Mui-disabled .MuiAutocomplete-popupIndicator': {
        color: `${getColor(EColors.pureBlack, 0.38)} !important`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: getColor(EColors.v3Dark) + ' !important',
    },
}));

type CurrencyPickerProps = {
    label?: string;
    error?: FieldError;
    value?: number;
    disabled?: boolean;
    required?: boolean;
    onChange?: (value?: number) => void;
    onBlur?: () => void;
    placeholder?: string;
    defaultCurrencyCode?: string;
};

export const CurrencyPicker = React.forwardRef<HTMLDivElement, CurrencyPickerProps>(
    ({ value, onChange, label, error, disabled, required, placeholder, onBlur, defaultCurrencyCode, ...rest }, ref) => {
        const currencies = useCurrencies();
        const isLoading = useCurrenciesLoading();
        const [inputValue, setInputValue] = React.useState('');
        const [isFocused, setIsFocused] = React.useState(false);

        // Sort currencies by code and create options
        const sortedCurrencies = React.useMemo(() => {
            return [...currencies].sort((a, b) => a.code.localeCompare(b.code));
        }, [currencies]);

        // Find default currency by code
        const defaultCurrency = React.useMemo(() => {
            if (!defaultCurrencyCode || !currencies.length) return null;
            return findCurrencyByCode(defaultCurrencyCode) || null;
        }, [defaultCurrencyCode, currencies.length]);

        // Find the selected currency - use value if provided, otherwise use default
        const selectedCurrency = React.useMemo(() => {
            if (value) {
                return sortedCurrencies.find(c => c.id === value) || null;
            }
            return defaultCurrency;
        }, [value, sortedCurrencies, defaultCurrency]);

        // Set default currency on mount if no value is provided
        React.useEffect(() => {
            if (!value && defaultCurrency && onChange) {
                onChange(defaultCurrency.id);
            }
        }, [value, defaultCurrency, onChange]);

        // Filter function to search by code, name, or symbol
        const filterOptions = React.useCallback((options: Currency[], { inputValue }: { inputValue: string }) => {
            if (!inputValue) return options;

            const searchTerm = inputValue.toLowerCase().trim();
            return options.filter(
                currency =>
                    currency.code.toLowerCase().includes(searchTerm) ||
                    currency.name.toLowerCase().includes(searchTerm) ||
                    currency.symbol.toLowerCase().includes(searchTerm)
            );
        }, []);

        const handleChange = (_: React.SyntheticEvent, newValue: Currency | null) => {
            // If defaultCurrencyCode is set, prevent clearing (don't allow null)
            if (defaultCurrencyCode && !newValue) {
                return;
            }
            onChange?.(newValue ? newValue.id : undefined);
            // Clear input value when a selection is made
            if (newValue) {
                setInputValue('');
            }
        };

        const handleInputChange = (_: React.SyntheticEvent, newInputValue: string) => {
            setInputValue(newInputValue);
        };

        const handleFocus = () => {
            setIsFocused(true);
        };

        const handleBlur = () => {
            setIsFocused(false);
            onBlur?.();
        };

        // Determine if label should shrink: when there's a value, input value, or when focused
        const shouldShrinkLabel = !!selectedCurrency || !!value || !!inputValue || isFocused;

        const baseAutocompleteProps: Partial<AutocompleteProps<Currency, false, false, false>> = {
            options: sortedCurrencies,
            value: selectedCurrency,
            inputValue,
            onInputChange: handleInputChange,
            onChange: handleChange,
            disabled: disabled || isLoading,
            filterOptions,
            getOptionLabel: (option: Currency) => `${option.code} - ${option.name} (${option.symbol})`,
            isOptionEqualToValue: (option: Currency, val: Currency) => option.id === val.id,
            popupIcon: <KeyboardArrowDownIcon />,
            noOptionsText: 'No currencies found',
            ListboxProps: {
                style: {
                    maxHeight: ITEM_HEIGHT * ITEM_COUNT,
                    borderRadius: 12,
                },
            },
            renderInput: (params: AutocompleteRenderInputParams) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={!!error}
                    required={required}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    InputLabelProps={{
                        shrink: shouldShrinkLabel,
                    }}
                />
            ),
        };

        const autocompleteProps = defaultCurrencyCode
            ? ({ ...baseAutocompleteProps, disableClearable: true } as typeof baseAutocompleteProps & {
                  disableClearable: true;
              })
            : baseAutocompleteProps;

        return (
            <StyledFormControl ref={ref} required={required} disabled={disabled || isLoading}>
                <StyledAutocomplete
                    {...(autocompleteProps as unknown as AutocompleteProps<Currency, false, false, false>)}
                    error={!!error}
                    {...rest}
                />
                {!!error && (
                    <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500} sx={{ mt: 0.5 }}>
                        {error.message}
                    </Typography>
                )}
            </StyledFormControl>
        );
    }
);

CurrencyPicker.displayName = 'CurrencyPicker';
